import { useState, useEffect, useRef } from 'react';
import { Star, Trash2, Globe, Lock, ChevronLeft, ChevronRight, RotateCcw, Archive, ShieldAlert, RotateCw, Maximize2, Minimize2, X, Link2, Copy } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import './PhotoDetailModal.css';
import { Photo, PhotoShareLink } from '../../../types';
import { RootState } from '../../../store/store';

import { upsertRating } from '../../../slice/ratingsSlice';
import { addComment, setCommentsByPhoto } from '../../../slice/commentsSlice';
import { setPhotoTags, setPhotoTagsLoading, setPhotoTagsError, addPhotoTag, removePhotoTag, addTag } from '../../../slice/tagsSlice';
import { updatePhoto, deletePhoto } from '../../../slice/photosSlice';
import { ratingService, commentService, tagService, photoService, adminService } from '../../../services';
import { authUserSignal } from '../../../signals/authSignal';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getPhotoStreamUrl, getSharePageUrl } from '../../../utils/urlBuilder';
import { useAuthenticatedImage } from '../../../hooks/useAuthenticatedImage';
import ConfirmDialog from '../../common/ConfirmDialog';
import MetadataTable from '../../common/MetadataTable';
import { resolveDateTaken, formatDateTakenForUI } from '../../../utils/exifDate';
import { createLogger } from '../../../utils/logger';
import { useSwipeGesture } from '../../../hooks/useSwipeGesture';

interface PhotoDetailModalProps {
  photo: Photo;
  onClose: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  canNavigatePrevious?: boolean;
  canNavigateNext?: boolean;
}

type TabType = 'info' | 'comments' | 'tags';

const logger = createLogger('PhotoDetailModal');

// Bottom-sheet snap points (percentage of viewport height) - mobile only
// Starts nearly hidden (just the drag handle peeking above the photo) so the
// photo is fully visible on open; user drags/taps the handle up to expand.
const SHEET_SNAP_POINTS = [20, 50, 92];
const SHEET_DEFAULT_HEIGHT = SHEET_SNAP_POINTS[0];
const SHEET_CLOSE_THRESHOLD = 5;
const MOBILE_BREAKPOINT = 768;


export default function PhotoDetailModal({
  photo,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  canNavigatePrevious,
  canNavigateNext,
}: PhotoDetailModalProps) {
  const dispatch = useDispatch();
  const currentUser = authUserSignal.value;
  const { t } = useLanguage();

  const ratings = useSelector((state: RootState) => state.ratings);
  const comments = useSelector((state: RootState) => state.comments);
  const tags = useSelector((state: RootState) => state.tags);

  const [newCommentText, setNewCommentText] = useState('');
  const [tagSearchText, setTagSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [adminConfirmAction, setAdminConfirmAction] = useState<'rollback' | 'deleteBackup' | 'hardDelete' | null>(null);
  const [photoTransitioning, setPhotoTransitioning] = useState(false);
  const [shareLink, setShareLink] = useState<PhotoShareLink | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // View-only transforms (never persisted). Rotation resets per photo;
  // full view persists across next/prev navigation until explicitly exited.
  const [rotation, setRotation] = useState(0);
  const [isFullView, setIsFullView] = useState(false);

  // Mobile draggable/expandable bottom sheet state
  const [sheetHeight, setSheetHeight] = useState(SHEET_DEFAULT_HEIGHT); // vh %
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(SHEET_DEFAULT_HEIGHT);
  const dragMoved = useRef(false);

  // Ref for swipe detection on photo area
  const photoAreaRef = useRef<HTMLDivElement>(null);

  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

  const clampHeight = (vh: number) => Math.min(96, Math.max(SHEET_SNAP_POINTS[0], vh));

  const snapToNearest = (vh: number) => {
    const closest = SHEET_SNAP_POINTS.reduce((prev, curr) =>
      Math.abs(curr - vh) < Math.abs(prev - vh) ? curr : prev
    );
    return closest;
  };

  const handleDragStart = (clientY: number) => {
    if (!isMobile()) return;
    setIsDragging(true);
    dragMoved.current = false;
    dragStartY.current = clientY;
    dragStartHeight.current = sheetHeight;
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return;
    const deltaVh = ((dragStartY.current - clientY) / window.innerHeight) * 100;
    if (Math.abs(deltaVh) > 1) dragMoved.current = true;
    setSheetHeight(clampHeight(dragStartHeight.current + deltaVh));
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Tap (no real drag) on the handle toggles between collapsed and default expanded
    if (!dragMoved.current) {
      setSheetHeight(
        dragStartHeight.current <= SHEET_SNAP_POINTS[0]
          ? SHEET_SNAP_POINTS[1]
          : SHEET_SNAP_POINTS[0]
      );
      return;
    }

    if (sheetHeight <= SHEET_CLOSE_THRESHOLD) {
      onClose();
      return;
    }
    setSheetHeight(snapToNearest(sheetHeight));
  };

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);
    if (isMobile()) {
      setSheetHeight(SHEET_SNAP_POINTS[SHEET_SNAP_POINTS.length - 1]);
    }
  };

  // Global listeners while dragging (mouse) - touch handled via React's non-passive handlers

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const onMouseUp = () => handleDragEnd();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, sheetHeight]);

  // Get current user's rating and ID
  // Note: userId may be 0 if currentUser has stale auth data; instead, search for the latest rating in Redux
  const userId = currentUser?.id || 0;
  const photoRatings = ratings.byPhotoId[photo.id] || {};
  const userRating = Object.values(photoRatings).length > 0 && currentUser 
    ? Object.values(photoRatings)[Object.values(photoRatings).length - 1]?.stars || 0 
    : 0;

  // Check if current user can manage this photo (is admin or owner)
  const canManage = currentUser?.role === 'admin' || photo.uploaded_by === userId;
  // Strict admin-only gate (rollback/backup-delete/hard-delete are admin-scoped
  // on the backend regardless of ownership) and whether a pre-replace backup exists
  const isAdmin = currentUser?.role === 'admin';
  const hasEnhanceBackup = Boolean(photo.original_s3_key); // before use 'enhanced_at'

  // Get photo comments
  const photoComments = comments.byPhotoId[photo.id] || [];

  // Get photo tags
  const photoTagState = tags.byPhotoId[photo.id];
  const photoTags = photoTagState?.tagIds?.map(id => tags.byId[id]).filter(Boolean) || [];
  const tagsLoading = photoTagState?.loading || false;
  const tagsError = photoTagState?.error;

  // Load photo image via authenticated axios (with auto-retry on 403)
  const photoImageUrl = `/photos/${photo.id}/stream`;
  const { src: photoImageSrc, loading: photoImageLoading, error: photoImageError } = useAuthenticatedImage(photoImageUrl);

  // Rotation is view-only and never persisted; reset it whenever the photo changes.
  useEffect(() => {
    setRotation(0);
  }, [photo.id]);

  const handleRotate = () => setRotation(r => (r + 90) % 360);

  // Closing is context-aware: while in full view, X / ESC exits full view
  // only (back to the modal); otherwise it closes the modal entirely.
  const handleCloseOrExitFullView = () => {
    if (isFullView) {
      setIsFullView(false);
    } else {
      onClose();
    }
  };

  // Handle ESC key to close modal (or exit full view first, see above)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseOrExitFullView();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, isFullView]);

  // Handle keyboard arrow navigation
  useEffect(() => {
    if (!onNavigatePrevious || !onNavigateNext) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'ArrowLeft' && canNavigatePrevious) {
        e.preventDefault();
        setPhotoTransitioning(true);
        onNavigatePrevious();
      } else if (e.key === 'ArrowRight' && canNavigateNext) {
        e.preventDefault();
        setPhotoTransitioning(true);
        onNavigateNext();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [onNavigatePrevious, onNavigateNext, canNavigatePrevious, canNavigateNext]);

  // Handle touch swipe navigation
  useSwipeGesture(photoAreaRef, {
    threshold: 40,
    onSwipeLeft: () => {
      if (canNavigateNext && onNavigateNext) {
        setPhotoTransitioning(true);
        onNavigateNext();
      }
    },
    onSwipeRight: () => {
      if (canNavigatePrevious && onNavigatePrevious) {
        setPhotoTransitioning(true);
        onNavigatePrevious();
      }
    },
  });

  // Clear transitioning state when new photo finishes loading
  useEffect(() => {
    if (!photoImageLoading) {
      setPhotoTransitioning(false);
    }
  }, [photoImageLoading]);

   // Fetch comments when photo opens
   useEffect(() => {
     const fetchComments = async () => {
       try {
         const fetchedComments = await commentService.getPhotoComments(photo.id);
         dispatch(setCommentsByPhoto({ photoId: photo.id, comments: fetchedComments }));
       } catch (err) {
         logger.error('Failed to load comments:', err);
       }
     };
     fetchComments();
   }, [photo.id, dispatch]);

   // Fetch the current public share link state when the modal opens (owner/admin only)
   useEffect(() => {
     if (!canManage) return;
     setShareLinkCopied(false);
     const fetchShareLink = async () => {
       try {
         const link = await photoService.getShareLink(photo.id);
         setShareLink(link);
       } catch (err) {
         logger.error('Failed to load share link:', err);
       }
     };
     fetchShareLink();
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [photo.id, canManage]);

   // Fetch tags when tags tab is active
   useEffect(() => {
     if (activeTab === 'tags' && !photoTagState) {
       const fetchTags = async () => {
         try {
           dispatch(setPhotoTagsLoading({ photoId: photo.id, loading: true }));
           const fetchedTags = await tagService.getPhotoTags(photo.id);
           dispatch(setPhotoTags({ photoId: photo.id, tags: fetchedTags }));
         } catch (err) {
           logger.error('Failed to load tags:', err);
           dispatch(setPhotoTagsError({ photoId: photo.id, error: 'Failed to load tags' }));
         }
       };
       fetchTags();
     }
   }, [activeTab, photo.id, photoTagState, dispatch]);

    const handleRating = async (stars: 1 | 2 | 3 | 4 | 5) => {
      if (!currentUser) return;
      
      // Safety check: warn if userId is 0 (likely stale cached user data or pre-migration JWT token)
      if (userId === 0) {
        logger.warn('[PhotoDetailModal] Warning: userId is 0; your session may use an old authentication token. Please log out and log back in to refresh.');
      }
      
      try {
        setLoading(true);
        const savedRating = await ratingService.setRating(photo.id, stars);
        // Dispatch the actual saved rating from backend to ensure correct user_id
        dispatch(upsertRating(savedRating));
      } catch (err) {
        logger.error('Failed to set rating:', err);
      } finally {
        setLoading(false);
      }
    };

   const handleAddComment = async () => {
     if (!currentUser || !newCommentText.trim()) return;
     try {
       setLoading(true);
       const comment = await commentService.createComment(photo.id, newCommentText);
       dispatch(addComment(comment));
       setNewCommentText('');
     } catch (err) {
       logger.error('Failed to add comment:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleAddTag = async (tagId: number) => {
     if (!currentUser) return;
     try {
       setLoading(true);
       await tagService.addTagToPhoto(photo.id, tagId);
       const tag = tags.byId[tagId];
       if (tag) {
         dispatch(addPhotoTag({ photoId: photo.id, tag }));
       }
       setTagSearchText('');
     } catch (err) {
       logger.error('Failed to add tag:', err);
     } finally {
       setLoading(false);
     }
   };

  const handleRemoveTag = async (tagId: number) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      await tagService.removeTagFromPhoto(photo.id, tagId);
      dispatch(removePhotoTag({ photoId: photo.id, tagId }));
    } catch (err) {
      logger.error('Failed to remove tag:', err);
    } finally {
      setLoading(false);
    }
  };

   const handleCreateAndAddTag = async (name: string, type: 'person' | 'place' | 'year' | 'event' | 'custom') => {
     if (!currentUser || !name.trim()) return;
     try {
       setLoading(true);
       // Create the tag
       const newTag = await tagService.createTag(name.trim(), type);
       // Add to global tags list
       dispatch(addTag(newTag));
       // Add to this photo
       await tagService.addTagToPhoto(photo.id, newTag.id);
       dispatch(addPhotoTag({ photoId: photo.id, tag: newTag }));
       setTagSearchText('');
     } catch (err) {
       logger.error('Failed to create and add tag:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleChangeVisibility = async () => {
     if (!canManage || loading) return;
     try {
       setLoading(true);
       const newVisibility = photo.visibility === 'public' ? 'private' : 'public';
       const updatedPhoto = await photoService.updateVisibility(photo.id, newVisibility);
       dispatch(updatePhoto(updatedPhoto));
     } catch (err) {
       logger.error('Failed to change visibility:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleDeletePhoto = async () => {
     if (!canManage) {
       if (currentUser && userId === 0) {
         alert('Your session appears to be using an old authentication token. Please log out and log back in to refresh your session before deleting photos.');
       }
       return;
     }
     try {
       setLoading(true);
       await photoService.deletePhoto(photo.id);
       dispatch(deletePhoto(photo.id));
       onClose();
     } catch (err) {
       logger.error('Failed to delete photo:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleGenerateShareLink = async () => {
     if (!canManage || shareLinkLoading) return;
     try {
       setShareLinkLoading(true);
       const link = await photoService.createShareLink(photo.id);
       setShareLink(link);
     } catch (err) {
       logger.error('Failed to generate share link:', err);
     } finally {
       setShareLinkLoading(false);
     }
   };

   const handleCopyShareLink = async () => {
     if (!shareLink) return;
     try {
       await navigator.clipboard.writeText(getSharePageUrl(shareLink.token));
       setShareLinkCopied(true);
       setTimeout(() => setShareLinkCopied(false), 2000);
     } catch (err) {
       logger.error('Failed to copy share link:', err);
     }
   };

   const handleRevokeShareLink = async () => {
     if (!canManage || shareLinkLoading) return;
     try {
       setShareLinkLoading(true);
       await photoService.revokeShareLink(photo.id);
       setShareLink(null);
     } catch (err) {
       logger.error('Failed to revoke share link:', err);
     } finally {
       setShareLinkLoading(false);
     }
   };

   const handleRollbackPhoto = async () => {
     if (!isAdmin) return;
     try {
       setLoading(true);
       const updatedPhoto = await adminService.rollbackPhoto(photo.id);
       dispatch(updatePhoto(updatedPhoto));
       setAdminConfirmAction(null);
     } catch (err) {
       logger.error('Failed to roll back photo:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleDeletePhotoBackup = async () => {
     if (!isAdmin) return;
     try {
       setLoading(true);
       const updatedPhoto = await adminService.deletePhotoBackup(photo.id);
       dispatch(updatePhoto(updatedPhoto));
       setAdminConfirmAction(null);
     } catch (err) {
       logger.error('Failed to delete photo backup:', err);
     } finally {
       setLoading(false);
     }
   };

   const handleHardDeletePhoto = async () => {
     if (!isAdmin) return;
     try {
       setLoading(true);
       await adminService.hardDeletePhoto(photo.id);
       dispatch(deletePhoto(photo.id));
       onClose();
     } catch (err) {
       logger.error('Failed to permanently delete photo:', err);
     } finally {
       setLoading(false);
     }
   };

  // Get available tags (not already on photo) filtered by search
  const photoTagIds = photoTags.map(t => t.id);
  const availableTags = tags.allIds
    .map(id => tags.byId[id])
    .filter(tag => tag && !photoTagIds.includes(tag.id))
    .filter(tag => tag.name.toLowerCase().includes(tagSearchText.toLowerCase()));

  return (
    <div className="photo-modal-overlay" onClick={handleCloseOrExitFullView}>
      {/* Toolbar: rotate, full view toggle, close */}
      <div className="photo-modal-toolbar">
        <button
          className="photo-modal-toolbar-btn"
          onClick={(e) => { e.stopPropagation(); handleRotate(); }}
          title="Rotate"
          aria-label="Rotate photo"
        >
          <RotateCw size={20} strokeWidth={1.5} />
        </button>
        <button
          className="photo-modal-toolbar-btn"
          onClick={(e) => { e.stopPropagation(); setIsFullView(v => !v); }}
          title={isFullView ? 'Exit full view' : 'Full view'}
          aria-label="Toggle full view"
        >
          {isFullView ? <Minimize2 size={20} strokeWidth={1.5} /> : <Maximize2 size={20} strokeWidth={1.5} />}
        </button>
        <button
          className="photo-modal-toolbar-btn"
          onClick={(e) => { e.stopPropagation(); handleCloseOrExitFullView(); }}
          title="Close"
          aria-label="Close"
        >
          <X size={20} strokeWidth={1.5} />
        </button>
      </div>

      {/* Main container */}
      <div className={`photo-modal-container ${isFullView ? 'fullview' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Left: Photo with navigation buttons */}
        <div className="photo-modal-photo-wrapper">
          {/* Previous button */}
          {canNavigatePrevious && onNavigatePrevious && (
            <button
              className="photo-modal-nav-btn photo-modal-nav-btn-prev"
              onClick={onNavigatePrevious}
              title="Previous photo (← arrow)"
              aria-label="Previous photo"
            >
              <ChevronLeft size={32} strokeWidth={1.5} />
            </button>
          )}

          {/* Photo */}
          <div ref={photoAreaRef} className="photo-modal-photo" style={{ touchAction: 'pinch-zoom', position: 'relative' }}>
          {(photoImageLoading || photoTransitioning) && (
            <div className="photo-modal-loading-overlay">
              <div className="photo-modal-loading-spinner" />
              <div className="photo-modal-loading-text">Loading photo...</div>
            </div>
          )}
          {photoImageError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              background: 'rgba(255, 100, 100, 0.1)',
              color: 'rgba(255, 100, 100, 0.9)',
              fontSize: '0.9rem',
              textAlign: 'center',
              padding: '1rem',
            }}>
              Failed to load image: {photoImageError}
            </div>
          )}
          {photoImageSrc && (
            <img
              src={photoImageSrc}
              alt={photo.description || 'Family moment'}
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          )}
          </div>

          {/* Next button */}
          {canNavigateNext && onNavigateNext && (
            <button
              className="photo-modal-nav-btn photo-modal-nav-btn-next"
              onClick={onNavigateNext}
              title="Next photo (→ arrow)"
              aria-label="Next photo"
            >
              <ChevronRight size={32} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Right: Glass panel with tabs (hidden in full view) */}
        {!isFullView && (
        <div
          className={`photo-modal-panel ${isDragging ? 'dragging' : ''}`}
          style={isMobile() ? { height: `${sheetHeight}dvh` } : undefined}
        >
          {/* Drag handle - mobile only (hidden on desktop via CSS) */}
          <div
            className="photo-modal-drag-handle"
            onMouseDown={(e) => handleDragStart(e.clientY)}
            onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
            onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
            onTouchEnd={handleDragEnd}
          >
            <div className="photo-modal-drag-handle-bar" />
          </div>

          {/* Tab switcher */}
          <div className="photo-modal-tabs">

            {(['info', 'comments', 'tags'] as TabType[]).map((tab) => {
              const count = tab === 'comments' ? photoComments.length : tab === 'tags' ? photoTags.length : 0;
              return (
                <button
                  key={tab}
                  onClick={() => handleTabClick(tab)}
                  className={`photo-modal-tab-btn ${activeTab === tab ? 'active' : ''}`}
                >

                  <span>{tab}</span>
                  {count > 0 && (
                    <span className={`photo-modal-tab-badge ${activeTab === tab ? 'active' : 'inactive'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="photo-modal-content">
            {activeTab === 'info' && (
              <div className="photo-modal-content-scroll">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* Rating - moved to top */}
                  <div>
                    <div className="photo-modal-label" style={{ marginBottom: '0.75rem' }}>
                      Your Rating
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                       {[1, 2, 3, 4, 5].map(star => (
                         <button
                           key={star}
                           onClick={() => handleRating(star as 1 | 2 | 3 | 4 | 5)}
                           disabled={loading}
                           className={`photo-modal-star-btn ${star <= userRating ? 'active' : ''}`}
                         >
                           <Star
                             size={20}
                             strokeWidth={1.5}
                             fill={star <= userRating ? 'currentColor' : 'none'}
                           />
                         </button>
                       ))}

                     </div>
                    <div className="photo-modal-label" style={{ textTransform: 'none', letterSpacing: 'normal', fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: 0 }}>
                      Average: {photo.rating_avg ? Number(photo.rating_avg).toFixed(1) : 'N/A'} ({photo.rating_count || 0} ratings)
                    </div>
                  </div>

                  {/* Visibility - moved up */}
                  <div>
                    <div className="photo-modal-label">
                      Visibility
                    </div>
                    <div style={{ 
                      color: 'var(--text-primary)',
                      display: 'inline-block',
                      background: photo.visibility === 'public' ? 'rgba(0, 200, 100, 0.2)' : 'rgba(255, 100, 100, 0.2)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                    }}>
                      {photo.visibility === 'public' ? '🌐 Public' : '🔒 Private'}
                    </div>
                  </div>

                  {/* Date Taken - conditional rendering */}
                  {resolveDateTaken(photo) && (
                    <div>
                      <div className="photo-modal-label">
                        Date Taken
                      </div>
                      <div style={{ 
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem',
                      }}>
                        {formatDateTakenForUI(resolveDateTaken(photo)!)}
                      </div>
                    </div>
                  )}

                  {/* Manage Section (Owner/Admin only) */}
                  {canManage && (
                    <>
                      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }} />
                      
                      <div>
                        <div className="photo-modal-label" style={{ marginBottom: '0.75rem' }}>
                          {t('photoModal.manageSection')}
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {/* Visibility Toggle */}
                          <button
                            onClick={handleChangeVisibility}
                            disabled={loading}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'rgba(0, 102, 255, 0.1)',
                              border: '1px solid rgba(0, 102, 255, 0.3)',
                              borderRadius: '8px',
                              color: 'var(--text-primary)',
                              cursor: loading ? 'default' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'rgba(0, 102, 255, 0.2)')}
                            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'rgba(0, 102, 255, 0.1)')}
                          >
                            {photo.visibility === 'public' ? (
                              <>
                                <Lock size={16} />
                                {t('photoModal.makePrivate')}
                              </>
                            ) : (
                              <>
                                <Globe size={16} />
                                {t('photoModal.makePublic')}
                              </>
                            )}
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={loading}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'rgba(255, 100, 100, 0.1)',
                              border: '1px solid rgba(255, 100, 100, 0.3)',
                              borderRadius: '8px',
                              color: 'rgba(255, 100, 100, 0.9)',
                              cursor: loading ? 'default' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'rgba(255, 100, 100, 0.2)')}
                            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'rgba(255, 100, 100, 0.1)')}
                          >
                            <Trash2 size={16} />
                            {t('photoModal.deletePhoto')}
                          </button>

                          {/* Public Share Link */}
                          {shareLink ? (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={handleCopyShareLink}
                                disabled={shareLinkLoading}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem 1rem',
                                  background: 'rgba(0, 200, 150, 0.1)',
                                  border: '1px solid rgba(0, 200, 150, 0.3)',
                                  borderRadius: '8px',
                                  color: 'rgba(0, 200, 150, 0.9)',
                                  cursor: shareLinkLoading ? 'default' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.2)')}
                                onMouseLeave={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.1)')}
                              >
                                <Copy size={16} />
                                {shareLinkCopied ? t('photoModal.publicLinkCopied') : t('photoModal.copyPublicLink')}
                              </button>
                              <button
                                onClick={handleRevokeShareLink}
                                disabled={shareLinkLoading}
                                style={{
                                  padding: '0.75rem 1rem',
                                  background: 'rgba(255, 100, 100, 0.1)',
                                  border: '1px solid rgba(255, 100, 100, 0.3)',
                                  borderRadius: '8px',
                                  color: 'rgba(255, 100, 100, 0.9)',
                                  cursor: shareLinkLoading ? 'default' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(255, 100, 100, 0.2)')}
                                onMouseLeave={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(255, 100, 100, 0.1)')}
                              >
                                <Trash2 size={16} />
                                {t('photoModal.removePublicLink')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleGenerateShareLink}
                              disabled={shareLinkLoading}
                              style={{
                                padding: '0.75rem 1rem',
                                background: 'rgba(0, 200, 150, 0.1)',
                                border: '1px solid rgba(0, 200, 150, 0.3)',
                                borderRadius: '8px',
                                color: 'rgba(0, 200, 150, 0.9)',
                                cursor: shareLinkLoading ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.2)')}
                              onMouseLeave={(e) => !shareLinkLoading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.1)')}
                            >
                              <Link2 size={16} />
                              {t('photoModal.generatePublicLink')}
                            </button>
                          )}

                          {/* Admin-only: Restore Original + Delete Backup (only when a pre-replace backup exists) */}
                          {isAdmin && hasEnhanceBackup && (
                            <>
                              <button
                                onClick={() => setAdminConfirmAction('rollback')}
                                disabled={loading}
                                style={{
                                  padding: '0.75rem 1rem',
                                  background: 'rgba(0, 200, 150, 0.1)',
                                  border: '1px solid rgba(0, 200, 150, 0.3)',
                                  borderRadius: '8px',
                                  color: 'rgba(0, 200, 150, 0.9)',
                                  cursor: loading ? 'default' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.2)')}
                                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'rgba(0, 200, 150, 0.1)')}
                              >
                                <RotateCcw size={16} />
                                {t('photoModal.restoreOriginal')}
                              </button>

                              <button
                                onClick={() => setAdminConfirmAction('deleteBackup')}
                                disabled={loading}
                                style={{
                                  padding: '0.75rem 1rem',
                                  background: 'rgba(255, 170, 0, 0.1)',
                                  border: '1px solid rgba(255, 170, 0, 0.3)',
                                  borderRadius: '8px',
                                  color: 'rgba(255, 170, 0, 0.9)',
                                  cursor: loading ? 'default' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'rgba(255, 170, 0, 0.2)')}
                                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'rgba(255, 170, 0, 0.1)')}
                              >
                                <Archive size={16} />
                                {t('photoModal.deleteBackup')}
                              </button>
                            </>
                          )}

                          {/* Admin-only: permanent hard delete (DB + all S3 objects), no precondition */}
                          {isAdmin && (
                            <button
                              onClick={() => setAdminConfirmAction('hardDelete')}
                              disabled={loading}
                              style={{
                                padding: '0.75rem 1rem',
                                background: 'rgba(180, 0, 0, 0.15)',
                                border: '1px solid rgba(180, 0, 0, 0.4)',
                                borderRadius: '8px',
                                color: 'rgba(220, 60, 60, 1)',
                                cursor: loading ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'rgba(180, 0, 0, 0.25)')}
                              onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'rgba(180, 0, 0, 0.15)')}
                            >
                              <ShieldAlert size={16} />
                              {t('photoModal.hardDeletePhoto')}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Metadata Table */}
                  <div>
                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginBottom: '1rem' }} />
                    <div className="photo-modal-label" style={{ marginBottom: '0.75rem' }}>
                      Metadata
                    </div>
                    <MetadataTable data={photo.exif_data} />
                  </div>

                </div>
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="photo-modal-comments">
                {/* Comments list */}
                <div className="photo-modal-comments-list">
                  {photoComments.length > 0 ? (
                    photoComments.map(comment => (
                      <div key={comment.id} className="photo-modal-comment">
                        <div className="photo-modal-comment-author">
                          {comment.user_name || `User ${comment.user_id}`}
                        </div>
                        <div className="photo-modal-comment-text">
                          {comment.text}
                        </div>
                        <div className="photo-modal-comment-date">
                          {new Date(comment.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric' 
                          })}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="photo-modal-muted">
                      No comments yet
                    </div>
                  )}

                </div>

                {/* Add comment */}
                <div className="photo-modal-comments-input">
                  <input
                    type="text"
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder="Add a comment..."
                    disabled={loading || !currentUser}
                    className="photo-modal-input"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={loading || !currentUser || !newCommentText.trim()}
                    className="photo-modal-btn"
                  >
                    Send
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'tags' && (
              <div className="photo-modal-content-scroll">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {tagsLoading ? (
                    <div className="photo-modal-muted">
                      Loading tags...
                    </div>
                  ) : tagsError ? (

                    <div style={{ 
                      fontSize: '0.9rem', 
                      color: 'rgba(255, 100, 100, 0.8)', 
                      textAlign: 'center', 
                      padding: '2rem 0' 
                    }}>
                      {tagsError}
                    </div>
                  ) : (
                    <>
                      {/* Current tags */}
                      <div>
                        <div className="photo-modal-label" style={{ marginBottom: '0.75rem' }}>
                          Current Tags
                        </div>

                        {photoTags.length > 0 ? (
                          <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '0.75rem' 
                          }}>
                            {photoTags.map(tag => (
                              <div
                                key={tag.id}
                                className="photo-modal-tag-chip"
                                style={{
                                  background: tag.color ? `${tag.color}30` : undefined,
                                  border: `1px solid ${tag.color || 'var(--pm-tag-chip-border)'}`,
                                }}
                              >
                                {tag.type === 'person' && '👤'}
                                {tag.type === 'place' && '📍'}
                                {tag.type === 'year' && '📅'}
                                {tag.type === 'event' && '🎉'}
                                {tag.type === 'custom' && '🏷️'}
                                <span>{tag.name}</span>
                                {currentUser && (
                                  <button
                                    onClick={() => handleRemoveTag(tag.id)}
                                    disabled={loading}
                                    className="photo-modal-tag-chip-remove"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="photo-modal-label" style={{ textTransform: 'none', letterSpacing: 'normal', marginBottom: 0 }}>
                            No tags yet
                          </div>
                        )}
                      </div>


                      {/* Add tags */}
                      {currentUser && (
                        <div>
                          <div className="photo-modal-label" style={{ marginBottom: '0.75rem' }}>
                            Add Tags
                          </div>

                          <input
                            type="text"
                            value={tagSearchText}
                            onChange={(e) => setTagSearchText(e.target.value)}
                            placeholder="Search tags..."
                            disabled={loading}
                            className="photo-modal-input"
                            style={{ marginBottom: '0.75rem' }}
                          />
                          {tagSearchText && availableTags.length > 0 && (
                            <div style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '0.5rem',
                              maxHeight: '200px',
                              overflowY: 'auto',
                            }}>
                              {availableTags.slice(0, 10).map(tag => (
                                <button
                                  key={tag.id}
                                  onClick={() => handleAddTag(tag.id)}
                                  disabled={loading}
                                  className="photo-modal-tag-suggestion"
                                  style={{
                                    background: tag.color ? `${tag.color}20` : undefined,
                                    border: tag.color ? `1px solid ${tag.color}` : undefined,
                                    cursor: loading ? 'default' : 'pointer',
                                  }}
                                >

                                  {tag.type === 'person' && '👤'}
                                  {tag.type === 'place' && '📍'}
                                  {tag.type === 'year' && '📅'}
                                  {tag.type === 'event' && '🎉'}
                                  {tag.type === 'custom' && '🏷️'}
                                  <span>{tag.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {tagSearchText && availableTags.length === 0 && (
                            <div>
                              <div className="photo-modal-label" style={{ textTransform: 'none', letterSpacing: 'normal', marginBottom: '0.75rem' }}>
                                + Create "{tagSearchText}" as:
                              </div>

                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '0.5rem',
                              }}>
                                {(['person', 'place', 'year', 'event', 'custom'] as const).map(type => (
                                  <button
                                    key={type}
                                    onClick={() => handleCreateAndAddTag(tagSearchText, type)}
                                    disabled={loading}
                                    style={{
                                      padding: '0.5rem 0.75rem',
                                      background: 'rgba(0, 200, 100, 0.1)',
                                      border: '1px solid rgba(0, 200, 100, 0.3)',
                                      borderRadius: '16px',
                                      fontSize: '0.825rem',
                                      color: 'rgba(0, 255, 100, 0.9)',
                                      fontWeight: 500,
                                      cursor: loading ? 'default' : 'pointer',
                                      transition: 'all 0.2s',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {type === 'person' && '👤 '}
                                    {type === 'place' && '📍 '}
                                    {type === 'year' && '📅 '}
                                    {type === 'event' && '🎉 '}
                                    {type === 'custom' && '🏷️ '}
                                    {type}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('photoModal.deletePhotoConfirmTitle')}
        message={t('photoModal.deletePhotoConfirmMessage')}
        confirmLabel={t('photoModal.deletePhoto')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        isLoading={loading}
        onConfirm={handleDeletePhoto}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Confirm Admin Action Dialog (rollback / delete backup / hard delete) */}
      <ConfirmDialog
        isOpen={adminConfirmAction !== null}
        title={
          adminConfirmAction === 'rollback'
            ? t('photoModal.restoreOriginalConfirmTitle')
            : adminConfirmAction === 'deleteBackup'
            ? t('photoModal.deleteBackupConfirmTitle')
            : t('photoModal.hardDeletePhotoConfirmTitle')
        }
        message={
          adminConfirmAction === 'rollback'
            ? t('photoModal.restoreOriginalConfirmMessage')
            : adminConfirmAction === 'deleteBackup'
            ? t('photoModal.deleteBackupConfirmMessage')
            : t('photoModal.hardDeletePhotoConfirmMessage')
        }
        confirmLabel={
          adminConfirmAction === 'rollback'
            ? t('photoModal.restoreOriginal')
            : adminConfirmAction === 'deleteBackup'
            ? t('photoModal.deleteBackup')
            : t('photoModal.hardDeletePhoto')
        }
        cancelLabel={t('common.cancel')}
        variant="danger"
        isLoading={loading}
        onConfirm={
          adminConfirmAction === 'rollback'
            ? handleRollbackPhoto
            : adminConfirmAction === 'deleteBackup'
            ? handleDeletePhotoBackup
            : handleHardDeletePhoto
        }
        onCancel={() => setAdminConfirmAction(null)}
      />
    </div>
  );
}
